import { BlockScheduleModel } from '../models/blockschedule.model';
import { ProjectModel } from '../models/project.model';

type ProjectLike = Partial<Pick<ProjectModel, 'projectNumber' | 'projectName' | 'commProject' | 'prefix'>> | null | undefined;
type SkidLike = {
  tmeicSkidNo?: number | null;
  name?: string | null;
  blockName?: string | null;
  integratorSkidSerialNo?: string | null;
} | null | undefined;

const INVALID_FILE_SEGMENT = /[<>:"/\\|?*\u0000-\u001F]+/g;

function sanitizeSegment(value: string | number | null | undefined): string {
  return String(value ?? '')
    .trim()
    .replace(INVALID_FILE_SEGMENT, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function ensureCxPrefix(value: string): string {
  if (!value) {
    return '';
  }

  return value.toUpperCase().startsWith('CX') ? value : `CX${value}`;
}

function getProjectSegments(project: ProjectLike): string[] {
  if (!project) {
    return [];
  }

  const cxNumber = sanitizeSegment(project.commProject || project.projectNumber);
  const projectName = sanitizeSegment(project.projectName);

  return [ensureCxPrefix(cxNumber), projectName].filter(Boolean);
}

function getSkidSegments(skid: SkidLike): string[] {
  if (!skid) {
    return [];
  }

  const skidNo = skid.tmeicSkidNo != null ? sanitizeSegment(`MVPS${skid.tmeicSkidNo}`) : '';
  const skidName = sanitizeSegment(skid.name || skid.blockName || skid.integratorSkidSerialNo);

  return [skidNo, skidName].filter(Boolean);
}

export function getFileExtension(fileName: string | null | undefined, fallback = 'bin'): string {
  const normalized = String(fileName || '').trim();
  const extension = normalized.includes('.') ? normalized.split('.').pop() : '';
  const sanitized = sanitizeSegment(extension);
  return sanitized || sanitizeSegment(fallback) || 'bin';
}

export function stripFileExtension(fileName: string | null | undefined): string {
  const normalized = String(fileName || '').trim();
  const lastDotIndex = normalized.lastIndexOf('.');
  if (lastDotIndex <= 0) {
    return sanitizeSegment(normalized);
  }

  return sanitizeSegment(normalized.slice(0, lastDotIndex));
}

export function buildProjectDownloadFileName(
  project: ProjectLike,
  category: string,
  extension: string,
  contextParts: Array<string | number | null | undefined> = [],
  options?: { variant?: string | null }
): string {
  const parts = [
    ...getProjectSegments(project),
    sanitizeSegment(category),
    ...contextParts.map((part) => sanitizeSegment(part)).filter(Boolean),
    sanitizeSegment(options?.variant),
  ].filter(Boolean);

  const baseName = parts.join('_') || sanitizeSegment(category) || 'download';
  const normalizedExtension = sanitizeSegment(extension).replace(/^\./, '') || 'bin';
  return `${baseName}.${normalizedExtension}`;
}

function formatTimestamp(value: Date = new Date()): string {
  const pad = (part: number): string => part.toString().padStart(2, '0');
  return [
    value.getFullYear(),
    pad(value.getMonth() + 1),
    pad(value.getDate()),
    '_',
    pad(value.getHours()),
    pad(value.getMinutes()),
    pad(value.getSeconds()),
  ].join('');
}

export function buildChecksheetPdfFileName(
  project: ProjectLike,
  checklistShortName: string | number | null | undefined,
  generatedAt: Date = new Date()
): string {
  return buildProjectDownloadFileName(project, '', 'pdf', [
    checklistShortName,
    formatTimestamp(generatedAt),
  ]);
}

function getEquipmentSkidDisplayName(project: ProjectLike, skid: SkidLike): string {
  const customName = String(skid?.name || skid?.blockName || '').trim();
  if (customName) {
    return customName;
  }

  const configuredPrefix = String(project?.prefix || '').trim();
  const prefix = !configuredPrefix || configuredPrefix.toLowerCase() === 'skid' ? 'MVPS' : configuredPrefix;
  return `${prefix} #${skid?.tmeicSkidNo ?? ''}`.trim();
}

export function buildEquipmentChecklistPdfFileName(
  project: ProjectLike,
  templateName: string | number | null | undefined,
  skid: SkidLike
): string {
  const parts = [
    sanitizeSegment(project?.projectNumber || project?.commProject),
    sanitizeSegment(project?.projectName),
    sanitizeSegment(templateName),
    sanitizeSegment(getEquipmentSkidDisplayName(project, skid)),
  ].filter(Boolean);

  return `${parts.join('_') || 'checksheet'}.pdf`;
}

export function buildProjectFormPdfFileName(
  project: ProjectLike,
  formTemplateNumber: string | number | null | undefined,
  formName: string | number | null | undefined,
  generatedAt: Date = new Date()
): string {
  return buildProjectDownloadFileName(project, '', 'pdf', [
    formTemplateNumber,
    formName,
    formatTimestamp(generatedAt),
  ]);
}

export function buildProjectAssetDownloadFileName(
  project: ProjectLike,
  category: string,
  originalFileName: string | null | undefined,
  options?: {
    skid?: SkidLike;
    variant?: string | null;
    contextParts?: Array<string | number | null | undefined>;
    fallbackExtension?: string;
  }
): string {
  const extension = getFileExtension(originalFileName, options?.fallbackExtension || 'bin');
  const baseFileName = stripFileExtension(originalFileName);
  const contextParts = [
    ...getSkidSegments(options?.skid),
    ...(options?.contextParts ?? []),
    baseFileName,
  ];

  return buildProjectDownloadFileName(project, category, extension, contextParts, {
    variant: options?.variant,
  });
}

export function buildProjectFolderZipFileName(
  project: ProjectLike,
  category: string,
  folderName: string | null | undefined
): string {
  return buildProjectDownloadFileName(project, category, 'zip', [folderName]);
}
